import { CAMPAIGN_TEMPLATE_IDS, normalizeCampaignContent, type CampaignContentConfig } from './campaign-config';
/** Section catalog — the complete list of sections available in lead reports.
 * Each entry maps to a section built by reportDataAdapter.buildSections(). */
export interface CatalogSection {
  id: string;
  group: 'overview' | 'dossier' | 'engagement' | 'opportunities';
  defaultTitle: string;
  icon: string;
  isrTab: string;
  description: string;
}

export const SECTION_CATALOG: CatalogSection[] = [
  // Overview (cover page)
  { id: 'briefing', group: 'overview', defaultTitle: 'Executive intelligence summary', icon: 'summary', isrTab: 'Briefing', description: 'High-level synthesis of the lead and their strategic context.' },
  { id: 'fit', group: 'overview', defaultTitle: 'Why this lead fits', icon: 'target', isrTab: 'Briefing', description: 'ICP justification, top pain, value hook and trigger event.' },
  { id: 'next', group: 'overview', defaultTitle: 'Recommended next action', icon: 'move', isrTab: 'Guidance', description: 'The single most important move for the next conversation.' },
  // Dossier
  { id: 'person', group: 'dossier', defaultTitle: 'Professional profile', icon: 'person', isrTab: 'Overview', description: 'Role, location, key responsibilities and expertise.' },
  { id: 'company', group: 'dossier', defaultTitle: 'Company context', icon: 'company', isrTab: 'Overview', description: 'Industry, size, headquarters and key differentiators.' },
  { id: 'decision', group: 'dossier', defaultTitle: 'Decision mechanics', icon: 'decision', isrTab: 'Psychology', description: 'Buyer archetype, decision role and decision-making style.' },
  { id: 'contacts', group: 'dossier', defaultTitle: 'Company & person contacts', icon: 'chat', isrTab: 'Overview', description: 'Contact grid: emails, phones, social profiles and identifiers for the company and the lead.' },
  { id: 'campaign', group: 'dossier', defaultTitle: 'Campaign context', icon: 'signal', isrTab: 'Campaign', description: 'Campaign objective and targeting rationale.' },
  // Engagement
  { id: 'opening', group: 'engagement', defaultTitle: 'Opening approach', icon: 'chat', isrTab: 'Guidance', description: 'How to open the conversation and talking points.' },
  { id: 'friction', group: 'engagement', defaultTitle: 'Friction & topics to avoid', icon: 'warning', isrTab: 'Briefing', description: 'Language to avoid, red flags and cultural sensitivities.' },
  { id: 'objections', group: 'engagement', defaultTitle: 'Anticipated objections', icon: 'shield', isrTab: 'Guidance', description: 'Predicted objections and counter-strategies.' },
  { id: 'starters', group: 'engagement', defaultTitle: 'Conversation starters', icon: 'chat', isrTab: 'Guidance', description: 'Tiered icebreakers tailored to recent events.' },
  { id: 'timing', group: 'engagement', defaultTitle: 'Engagement timing', icon: 'clock', isrTab: 'Guidance', description: 'Preferred channels and optimal send windows.' },
  // Opportunities
  { id: 'opportunities', group: 'opportunities', defaultTitle: 'Campaign opportunities', icon: 'opportunity', isrTab: 'Opportunities', description: 'Revenue opportunities tied to this campaign.' },
  { id: 'market', group: 'opportunities', defaultTitle: 'Market opportunities', icon: 'globe', isrTab: 'Opportunities', description: 'Broader market growth opportunities for the lead.' },
  { id: 'committee', group: 'opportunities', defaultTitle: 'Buying committee & stakeholders', icon: 'network', isrTab: 'Network', description: 'Internal colleagues and their decision influence.' },
];

export const DEFAULT_GROUP_ORDER: CatalogSection['group'][] = ['overview', 'dossier', 'engagement', 'opportunities'];

/** SQL-only sheets — not body sections; they render as their own pages.
 *  Selected/hidden the same way, but placement is fixed (timeline, then summary). */
export const SQL_ONLY_SHEETS: CatalogSection[] = [
  { id: 'activity-timeline', group: 'overview', defaultTitle: 'Activity timeline', icon: 'clock', isrTab: 'Activity', description: 'Every recorded note, status change, email, call and message in chronological order.' },
  { id: 'interaction-summary', group: 'overview', defaultTitle: 'Final interaction summary', icon: 'summary', isrTab: 'Activity', description: 'The saved summary of the final interaction with this lead.' },
];

/** Everything an admin can toggle: body sections + SQL-only sheets. */
export const ALL_SELECTABLE: CatalogSection[] = [...SECTION_CATALOG, ...SQL_ONLY_SHEETS];

export interface SectionConfig {
  /** Ordered array of section IDs — the order IS the render order. */
  signals?: string[];
  deleted?: boolean;
  campaign?: CampaignContentConfig;
  sections: string[];
  /** Which chapter sheets appear and in what sequence. */
  groupOrder: string[];
}

/** Default config: all sections in catalog order, default group order. */
export const DEFAULT_CONFIG: SectionConfig = {
  sections: ALL_SELECTABLE.map(s => s.id),
  groupOrder: [...DEFAULT_GROUP_ORDER],
};

/** Look up a section by ID. */
export const getSection = (id: string): CatalogSection | undefined =>
  ALL_SELECTABLE.find(s => s.id === id);

export type LeadTemplateId = 'lead-sql' | 'lead-mql';
export const GROUP_LABELS: Record<string, string> = {
  overview: 'Overview', dossier: 'Lead & company dossier',
  engagement: 'Engagement playbook', opportunities: 'Commercial opportunities',
};
export const selectableSections = (template: LeadTemplateId) => template === 'lead-mql' ? SECTION_CATALOG : ALL_SELECTABLE;

/** Read legacy configs without adding previously hidden content. Cover stays first. */
export function normalizeSectionConfig(value: unknown, template: LeadTemplateId): SectionConfig {
  if (value == null) return { sections: selectableSections(template).map(s => s.id), groupOrder: [...DEFAULT_GROUP_ORDER] };
  const raw = value as SectionConfig;
  if (!Array.isArray(raw.sections) || !raw.sections.every(id => typeof id === 'string') ||
      (raw.groupOrder !== undefined && (!Array.isArray(raw.groupOrder) || !raw.groupOrder.every(id => typeof id === 'string')))) {
    throw new Error('Invalid report template settings. Reload or contact an administrator.');
  }
  // An EMPTY sections array can never be saved through the Admin UI (validation
  // requires at least one section) — it means the template was never
  // configured. Treat it as unset: full default content. BUT a config WITH ids
  // that all fall outside this template's selectable set is an explicit
  // (mis)configuration — keep it empty so validateTemplateConfigs throws.
  if (raw.signals !== undefined && (!Array.isArray(raw.signals) || !raw.signals.every(id => typeof id === 'string'))) throw new Error('Invalid strategic signal settings.');
  const allowed = new Set(selectableSections(template).map(s => s.id));
  const sections = [...new Set(raw.sections)].filter(id => allowed.has(id));
  // MIGRATION: 'contacts' post-dates the first saved configs — a config saved
  // before it existed would hide it forever. Inject after 'company' (the
  // dossier anchor) so legacy setups show the contact grid by default.
  if (allowed.has('contacts') && !sections.includes('contacts') && sections.includes('company')) {
    sections.splice(sections.indexOf('company') + 1, 0, 'contacts');
  }
  return {
    ...(raw.signals ? { signals: raw.signals.filter(id => ['pain','trigger','hook'].includes(id)) } : {}),
    sections,
    groupOrder: ['overview', ...new Set([...(raw.groupOrder ?? []), ...DEFAULT_GROUP_ORDER].filter(id => id !== 'overview' && DEFAULT_GROUP_ORDER.includes(id as CatalogSection['group'])))],
  };
}

export function normalizeTemplateConfigs(raw: unknown): Record<string, SectionConfig> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid report template settings.');
  const configs = raw as Record<string, unknown>;
  return { 'lead-sql': normalizeSectionConfig(configs['lead-sql'], 'lead-sql'), 'lead-mql': normalizeSectionConfig(configs['lead-mql'], 'lead-mql'), ...Object.fromEntries(CAMPAIGN_TEMPLATE_IDS.map(id => { const v = configs[id] as SectionConfig | undefined; return [id, { sections: [], groupOrder: [], deleted: v?.deleted === true, campaign: normalizeCampaignContent(v?.campaign, id) }]; })) };
}

export function validateTemplateConfigs(configs: Record<string, SectionConfig>): void {
  for (const id of CAMPAIGN_TEMPLATE_IDS) {
    const cfg = configs[id];
    if (!cfg || cfg.deleted || id === 'campaign-export') continue;
    const c = normalizeCampaignContent(cfg.campaign, id);
    if (!c.graph && c.directory === 'none') throw new Error('Choose a graph or a directory for the campaign report.');
    if (['companies','both'].includes(c.directory) && !c.companyFields.length) throw new Error('Select at least one company field.');
    if (['leads','both'].includes(c.directory) && !c.leadFields.length) throw new Error('Select at least one lead field.');
  }
  for (const template of ['lead-sql', 'lead-mql'] as const) {
    if (!normalizeSectionConfig(configs[template], template).sections.length) {
      throw new Error(`Select at least one subsection for ${template === 'lead-sql' ? 'SQL' : 'MQL'}.`);
    }
  }
}

export function toggleSections(config: SectionConfig, ids: string[]): SectionConfig {
  const allSelected = ids.every(id => config.sections.includes(id));
  return { ...config, sections: allSelected ? config.sections.filter(id => !ids.includes(id)) : [...new Set([...config.sections, ...ids])] };
}

export function moveItem(items: string[], id: string, direction: -1 | 1): string[] {
  const index = items.indexOf(id), target = index + direction;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
